/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import gpaForecastServices from 'services/gpaforecast.services';
import gpaForecastDetailServices from 'services/gpaforecastDetail.services';
import GPAHeroCard from './GPAHeroCard';
import { gpaHeroCardTokens as T } from './gpaHeroCard.tokens';
import { genGradeOptions, genTermOptions } from './formCreateGpaForecastModal';
import { formatAgAreaLabel, subjectAreaOptions } from 'screen/course/courseConstant';

/**
 * GPA Calculator Revamp v1.1 (ClickUp #86ak9ab4g) — full-page rewrite.
 * Renders instead of the legacy GpaForecaster.tsx tree when `student.gpa_version === 2`.
 *
 * ============================================================================
 * REGRESSION FIXES, 2026-09-23. Read this before changing anything below.
 * ============================================================================
 * The first cut of this file (shipped to staging 2026-09-19) lost several
 * capabilities the legacy page had, and introduced three ways to corrupt a
 * student's own data. All are fixed here. Each fix is marked `FIX Bn` / `FIX Hn`
 * at its site and explained in
 * `00_source_of_truth/GPA_Forecaster_V2_Regressions_2026-09-23.md`.
 *
 *  B1  Add Course sent a nested {gpa_forecast, detail} body. The endpoint is a
 *      flat repository.insert() against CreateGpaForecastDetailDto, so the call
 *      could never succeed. Now sends the flat shape and attaches the detail to
 *      an EXISTING gpa_forecast row the user picks, as the legacy page did.
 *  B2  The grade dropdown was hardcoded to letters. A student on a 0-100 or
 *      IB 1-7 scale could not enter their grade, and opening the dropdown on an
 *      existing numeric grade could overwrite it with blank. Options now come
 *      from the block's grading scale via the shared genGradeOptions().
 *  B3  Credit ran Number(e.target.value) on every keystroke with save-on-blur,
 *      so clearing the field to retype saved credit 0 and silently dropped the
 *      course out of every GPA. Credit is now held as a string and validated
 *      before save.
 *  B4  Delete was offered on transcript-derived rows. The legacy page only ever
 *      allowed deleting manually-added rows. Restored: the control appears only
 *      when is_custom is true.
 *  B5  No way to pull newly-entered coursework across from Courses & Grades.
 *      Restored the sync call behind a "Refresh from Courses & Grades" button.
 *  H1  unweighted_point / weighted_point were not editable and were not sent on
 *      save. They are the mechanism for expressing a school's own point scale
 *      (Del Lago Academy, for instance, gives D and F no weighted value at all).
 *      Restored as editable inputs that prefill from the grade and set
 *      is_override when a human types over them.
 *  H2  Changing a grade left the stored points untouched, so the row held a new
 *      grade beside old points. Points now recompute on any grade or weighted
 *      change, unless the row is overridden.
 *  H5  The hero tiles never refreshed after an edit. They now remount on save.
 *
 * Design intent (Michelle, 09-19 and 09-23):
 *   - per-row chips showing which of the four GPAs a course counts toward
 *   - star marker on weighted rows
 *   - grading-scale normalization visible inline per row
 *   - college coursework tagged in place rather than floating at the bottom
 *   - prefill sensible defaults, then let a human change anything
 */

/**
 * ============================================================================
 * DESIGN PASS, 2026-09-24. Audited against gradmap-tokens.css v2 and
 * GradMap_Design_Rules.md v2. What changed and why:
 *
 *  D1  No literal values left. `borderRadius: '4px'` on inputs and the
 *      `rgba(18,29,49,.45)` modal scrim were the last two. Radius now follows
 *      the depth rule: page cards 16, the modal panel 16, controls pills.
 *  D2  Table headers were uppercase with letter-spacing. Rule 5 is sentence
 *      case everywhere, with all-caps reserved for two-character status flags.
 *  D3  Nothing renders below 11.5px any more. The page had 10px and 9.5px
 *      labels, which are under the approved caption size.
 *  D4  Mode chips carried their state in colour AND in opacity, and the dimmed
 *      pair measured about 2.8:1. Opacity is gone and a check mark carries the
 *      "counts" state alongside the colour, so colour is never the only signal.
 *  D5  The duplicate-course panel had a 3px left border clipped by the card's
 *      own 16px corner. Cards carry no border; the panel now says what it is
 *      with a labelled warning chip.
 *  D6  An excluded row carried three markers of one state: a sunken
 *      background, a repeated grade chip, and the reason text. The repeated
 *      grade chip is gone, since the Grade column already shows it.
 *  D7  Keyboard focus was invisible on every custom button. A scoped style
 *      block adds the token focus ring, since inline styles cannot express
 *      :focus-visible.
 *
 *  KNOWN DEVIATION, deliberate. The page inherits Manrope from App.css rather
 *  than Nunito Sans. Setting the correct face here alone would leave this one
 *  screen out of step with the app chrome around it. The font swap belongs to
 *  the global restyle, not to this ticket.
 * ============================================================================
 */

// ---------- layout ----------

const pageStyle: React.CSSProperties = {
  fontFamily: T.fontUi,
  color: T.ink,
  padding: `0 ${T.space5}`,
  maxWidth: '1200px',
  margin: '0 auto',
};

const panelStyle: React.CSSProperties = {
  background: T.surface,
  borderRadius: T.radiusSurface,
  padding: T.space5,
  marginBottom: T.space4,
  boxShadow: T.elevSurface,
};

const panelHeaderStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: T.space3,
  paddingBottom: T.space2,
  borderBottom: `1px solid ${T.hairline}`,
  flexWrap: 'wrap',
  gap: T.space2,
};

const panelTitleStyle: React.CSSProperties = {
  fontSize: T.textLg,
  fontWeight: T.weightBold,
  color: T.ink,
  margin: 0,
  letterSpacing: '-0.015em',
};

const panelStatusStyle = (status: 'locked' | 'in-progress' | 'planned'): React.CSSProperties => ({
  fontSize: T.textXs,
  fontWeight: T.weightSemibold,
  padding: '2px 10px',
  borderRadius: T.radiusControl,
  color: status === 'locked' ? T.inkMuted : status === 'in-progress' ? T.accentInk : T.warningInk,
  background: status === 'locked' ? T.sunken : status === 'in-progress' ? T.accentWash : T.warningWash,
  marginLeft: T.space2,
});

const yearSubtotalStyle: React.CSSProperties = {
  fontSize: T.textXs,
  color: T.inkMuted,
  fontWeight: T.weightMedium,
};

const yearSubtotalNumberStyle: React.CSSProperties = {
  color: T.inkStrong,
  fontWeight: T.weightBold,
  marginLeft: T.space1,
};

const addBtnStyle: React.CSSProperties = {
  padding: `6px ${T.space4}`,
  fontSize: T.textSm,
  cursor: 'pointer',
  background: T.accent,
  color: T.inkOnDark,
  border: 'none',
  borderRadius: T.radiusControl,
  fontWeight: T.weightSemibold,
  fontFamily: T.fontUi,
};

const secondaryBtnStyle: React.CSSProperties = {
  padding: `6px ${T.space4}`,
  fontSize: T.textSm,
  cursor: 'pointer',
  background: 'transparent',
  color: T.inkBody,
  border: `1px solid ${T.hairlineStrong}`,
  borderRadius: T.radiusControl,
  fontWeight: T.weightSemibold,
  fontFamily: T.fontUi,
};

/**
 * FIX (medium): the legacy page wrapped every table in a horizontal scroller
 * and used 44px touch targets. This table is wider than a phone viewport, so it
 * needs the wrapper or it is unusable on mobile.
 */
const tableScrollStyle: React.CSSProperties = {
  width: '100%',
  overflowX: 'auto',
  WebkitOverflowScrolling: 'touch',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  minWidth: '940px',
  fontSize: T.textSm,
  borderCollapse: 'collapse',
};

/**
 * D2. Sentence case, not uppercase. The design rules allow all-caps only on
 * two-character status flags, which is why UW and W below keep theirs.
 */
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: `${T.space2} ${T.space2}`,
  borderBottom: `1px solid ${T.hairline}`,
  color: T.inkMuted,
  fontSize: T.textXs,
  fontWeight: T.weightSemibold,
  letterSpacing: 0,
  whiteSpace: 'nowrap',
};

/**
 * The data columns on the right are centred, headers and values alike, so the
 * numbers line up as a block rather than drifting against ragged labels.
 */
const thCenterStyle: React.CSSProperties = {
  ...thStyle,
  textAlign: 'center',
};

/**
 * "Course Credits" wraps onto two lines rather than forcing the column as wide
 * as the label. Every other header stays on one line.
 */
const thCenterWrapStyle: React.CSSProperties = {
  ...thCenterStyle,
  whiteSpace: 'normal',
  width: '72px',
  lineHeight: 1.25,
};

/** Spans the two point columns. Its bottom border is the sub-header's job. */
const thGroupStyle: React.CSSProperties = {
  ...thCenterStyle,
  borderBottom: 'none',
  paddingBottom: '2px',
};

/** UW / W, sitting under the "Grade Points" group header. */
const thSubStyle: React.CSSProperties = {
  ...thCenterStyle,
  paddingTop: 0,
  letterSpacing: '0.04em',
};

const tdStyle: React.CSSProperties = {
  padding: `${T.space2} ${T.space2}`,
  borderBottom: `1px solid ${T.hairline}`,
  color: T.inkBody,
  verticalAlign: 'middle',
};

const rowStyle = (variant: 'default' | 'college' | 'excluded'): React.CSSProperties => ({
  background:
    variant === 'college' ? T.accentWash : variant === 'excluded' ? T.sunken : T.surface,
});

const chipInline = (opts: {
  color: string;
  bg: string;
  border?: string;
  size?: 'xs' | 'sm';
}): React.CSSProperties => ({
  display: 'inline-block',
  // D3. 11.5px is the approved caption floor. The previous 9.5 / 10.5 pair was
  // below it, so `xs` is now a padding difference rather than a size one.
  fontSize: T.textXs,
  fontWeight: T.weightSemibold,
  padding: opts.size === 'xs' ? '1px 7px' : '2px 8px',
  borderRadius: T.radiusControl,
  background: opts.bg,
  color: opts.color,
  border: opts.border ? `1px solid ${opts.border}` : 'none',
  marginLeft: '4px',
  whiteSpace: 'nowrap',
});

const MODE_CHIP_LABEL: Record<1 | 2 | 3 | 4, string> = {
  1: 'Unweighted',
  2: 'Weighted',
  3: 'Capped',
  4: 'Uncapped',
};

/*
  modeChipStyle removed 2026-09-29 along with the four-chip "Counts for"
  column. MODE_CHIP_LABEL is kept: rowCountsNote still uses it to name modes in
  the fallback case.
*/

const inputCenterStyle: React.CSSProperties = { textAlign: 'center' };

/**
 * D1. A control that takes a click is a pill. 4px was not a token value and
 * sat in the dead zone the radius rule exists to keep empty.
 */
const inputStyle: React.CSSProperties = {
  padding: `4px ${T.space3}`,
  fontSize: T.textSm,
  border: `1px solid ${T.hairlineStrong}`,
  borderRadius: T.radiusControl,
  fontFamily: T.fontUi,
  color: T.ink,
  background: T.surface,
  width: '100%',
  minWidth: '56px',
  boxSizing: 'border-box',
};

const invalidInputStyle: React.CSSProperties = {
  ...inputStyle,
  borderColor: T.dangerInk,
};

const labelStyle: React.CSSProperties = {
  fontSize: T.textXs,
  color: T.inkMuted,
  fontWeight: T.weightSemibold,
  display: 'block',
  marginBottom: T.space1,
};

/**
 * D7. Inline styles cannot express :focus-visible, so keyboard focus was
 * invisible on every button, select and input on this page. One scoped style
 * block, mounted once by the page, fixes that without a CSS file or a build
 * change. Scoped by data attribute so it cannot leak into the legacy page.
 */
const FocusStyles: React.FC = () => (
  <style>{`
    [data-gm-gpa-v2] button:focus-visible,
    [data-gm-gpa-v2] input:focus-visible,
    [data-gm-gpa-v2] select:focus-visible {
      outline: none;
      box-shadow: ${T.focusRing};
    }
    [data-gm-gpa-v2] select { -webkit-appearance: none; appearance: none; }
  `}</style>
);

// ---------- types ----------

interface Props {
  studentId: string | number | null | undefined;
}

/** One coursework block: a school + year + schedule + grading scale. */
interface BlockShape {
  gpaForecastId: number;
  studentId: number;
  seqNo: number;
  gradeNo: number;
  schoolName: string;
  schoolCode: string;
  schedule: string;
  gradingScale: string;
  /** Bishop's pattern: the transcript credit is the whole-year figure. */
  creditsAreAnnual: boolean;
}

interface RowShape extends BlockShape {
  courseType: 'HS' | 'COLLEGE';
  detailId: number;
  course: string;
  term: string;
  rawGrade: string;
  /** FIX B3 — held as a string so an in-progress edit is never coerced to 0. */
  rawCredit: string;
  isWeightPoint: boolean;
  summerCourse: boolean;
  /** FIX H1 — restored from the legacy page. Held as strings for the same reason as credit. */
  unweightedPoint: string;
  weightedPoint: string;
  /** FIX B4 — only manually-added rows may be deleted here. */
  isCustom: boolean;
  /** FIX H1/H2 — true once a human types over a prefilled point value. */
  isOverride: boolean;
  /**
   * A-G plumbing, 2026-09-23. The student picks these on every course in
   * Courses & Grades. `isAgApproved` is three-state: true, false, or null when
   * the row has not been re-synced since the backend columns were added.
   */
  agSubjectArea: string | null;
  typeOfClass: string | null;
  isAgApproved: boolean | null;
  /** Student confirmed this row duplicates a college row. Counts nowhere. */
  excludedAsDuplicate: boolean;
  /** This school prints one credit for the year, not per term (Bishop's). */
  creditsAreAnnual: boolean;
  /** The school's Schedule is not one we can map, so credit weighting is approximate. */
  scheduleUnrecognised: boolean;
  /** The block's Schedule, so the Term column can name the term correctly. */
  schedule: string;
  /**
   * This row is a Final for a course that also has term rows, so it repeats a
   * year already counted. Computed on the client from the loaded rows, the
   * same way markTermSummaryRows does it on the backend.
   */
  excludedAsTermSummary: boolean;
  dirty: boolean;
  saving: boolean;
}

// ---------- constants ----------

const GRADE_YEARS = [9, 10, 11, 12] as const;
const GRADE_LABEL: Record<number, string> = {
  9: '9th Grade',
  10: '10th Grade',
  11: '11th Grade',
  12: '12th Grade',
  13: 'College Coursework',
};

/** Statuses that carry no earned grade, so contribute to no GPA. */
const NON_GRADE_STATUSES = ['IP', 'PL', 'W', 'P', 'NP', 'CR', 'NC', ''];

const isEditableGradeStatus = (grade: string): boolean =>
  !NON_GRADE_STATUSES.includes((grade ?? '').trim().toUpperCase());

/**
 * The sentinel the rest of the app already uses for a course outside A-G
 * (see CourseConfig.tsx). Matching it rather than inventing a second rule.
 */
const NON_AG_SENTINEL = 'non a-g course';

/**
 * Schedule values the backend can map to a credit multiplier
 * (SCHEDULE_MULTIPLIERS in normalization/credit-normalization.ts). Anything else,
 * including the real value "Other", is counted at face value and flagged.
 */
const KNOWN_SCHEDULES = [
  'Semester', 'Standard', 'Half-credit', 'Half-Credit Semester',
  'Trimester', 'Quarter', 'Quarters', 'Yearly', 'Full Year',
];

/**
 * Three-state on purpose. `null` means we have no answer yet, because the row
 * predates the A-G columns and has not been re-synced. A null must not be
 * treated as Non A-G: that would quietly drop the course out of the UC numbers
 * while looking identical to a correct exclusion.
 *
 * Grade 13 (college) is approved by its nature; A-G is a high school
 * classification.
 */
const deriveAgApproved = (raw: any, gradeNo: number): boolean | null => {
  if (gradeNo === 13) return true;
  const area = (raw ?? '').toString().trim();
  if (!area) return null;
  return area.toLowerCase() !== NON_AG_SENTINEL;
};

/**
 * FIX B2 — grade options come from the block's grading scale.
 *
 * genGradeOptions() is the same helper the legacy add-course modal uses, so the
 * two pages cannot drift. It covers A-F, 0-100, 0-105, 1-100, 0-110, 0.0-4.0,
 * 0.0-5.0, 1-7 (IB), 1-10, 1-11 and 1-20.
 *
 * The status values are appended because they are valid entries on any scale
 * and genGradeOptions() does not return them.
 */
const gradeOptionsFor = (gradingScale: string): string[] => {
  // genGradeOptions() returns a union of array types ({value: number}[] | {value: string}[]).
  // Widen it to one array type first; the project's TypeScript version cannot call
  // .map() on a union of arrays (TS2349).
  const rawOpts: Array<string | { value: string | number }> = genGradeOptions(gradingScale) ?? [];
  const scaleOpts = rawOpts.map((o) => (typeof o === 'string' ? o : String(o.value)));
  if (!scaleOpts.length) return [];
  return [...scaleOpts, 'IP', 'PL', 'W', 'P', 'NP'];
};



const termOptionsFor = (schedule: string, gradeNo: number): string[] =>
  (genTermOptions(schedule, gradeNo) ?? []).map((o: any) =>
    typeof o === 'string' ? o : String(o.value)
  );

/**
 * What to CALL a term, 2026-09-24.
 *
 * The stored value for a quarter school's third quarter is the string
 * "Semester 3", and for a trimester school's third trimester it is also
 * "Semester 3". Michelle, on seeing that: "it's ridiculous." It is, and it
 * teaches a student the wrong word for their own transcript.
 *
 * genTermOptions now carries the right label beside the stored value, so this
 * reads the label back out of the same source rather than keeping a second
 * mapping that could drift. The stored value is never touched; renaming it is
 * a separate ticket with a migration behind it.
 */
const termLabel = (schedule: string, gradeNo: number, storedTerm: string): string => {
  const match = (genTermOptions(schedule, gradeNo) ?? []).find(
    (o: any) => typeof o !== 'string' && String(o.value) === storedTerm
  );
  return match && typeof match !== 'string' ? String(match.name) : storedTerm;
};

/**
 * A Final that repeats its own term grades, 2026-09-24.
 *
 * Michelle: "some schools will have a semester, trimester grades or quarter
 * grade then a final grade." When a course has both, the Final summarises the
 * terms rather than adding to them, so the backend skips it. This mirrors that
 * so the row can say why, rather than looking as though it counts.
 */
const markTermSummaries = (rows: RowShape[]): RowShape[] => {
  const isFinal = (t: string) => (t ?? '').trim().toLowerCase() === 'final';
  const key = (r: RowShape) =>
    `${r.gpaForecastId}::${r.seqNo}::${r.course.trim().toLowerCase()}`;

  const hasTermRow = new Set<string>();
  for (const r of rows) if (!isFinal(r.term)) hasTermRow.add(key(r));
  if (hasTermRow.size === 0) return rows;

  return rows.map((r) =>
    isFinal(r.term) && hasTermRow.has(key(r)) ? { ...r, excludedAsTermSummary: true } : r
  );
};

/**
 * Scales whose grade is a percent. These use the 90/80/70/60 cuts.
 * Mirrors PERCENT_NUMERIC_SCALES in the backend's src/utils.ts.
 */
const PERCENT_SCALES = ['0-100', '0-105', '0-110', '1-100'];

/**
 * Scales whose grade is a number on a fixed range. Dividing by these brings the
 * value onto the 4.0 scale.
 *
 * These divisors are the ones in the backend's
 * `gpa-forecast/normalization/grade-normalization.ts` (RAW_SCALE_DIVISORS), so
 * the prefill a student sees matches what the calculator computes. If one side
 * changes, change the other.
 *
 * `1-11` is included here and was added to the backend at the same time; it was
 * offered by the grading-scale dropdown but handled by neither, so an 11-point
 * school's grades normalized to nothing.
 */
const RAW_SCALE_DIVISORS: Record<string, number> = {
  '0.0-4.0': 1,
  '0.0-5.0': 1.25,
  '1-7': 1.75,
  '1-7 (IB)': 1.75,
  '1-10': 2.5,
  '1-11': 2.75,
  '1-20': 5.0,
};

/**
 * FIX H1/H2 — client-side prefill for the two point columns.
 *
 * Mirrors the backend's calculateGpaPoints() in `src/utils.ts` so a row shows
 * the same numbers before and after a round trip. Plus/minus collapses to the
 * base letter, matching the backend and UC's treatment of high school work.
 *
 * PERCENT SCALES. Both sides convert percent onto the 4.0 scale using the
 * standard 90/80/70/60 cuts. The backend used to pass the raw percent straight
 * through, which is where the "100 / 98" values in the points columns came
 * from; that was fixed in calculateGpaPoints on 2026-09-23, so the two agree.
 */
const prefillPoints = (
  gradingScale: string,
  grade: string,
  isWeightPoint: boolean
): { unweighted: number; weighted: number } | null => {
  const g = (grade ?? '').trim().toUpperCase();
  if (!isEditableGradeStatus(g)) return null;
  const bonus = isWeightPoint ? 1 : 0;

  let base: number | null = null;

  if (PERCENT_SCALES.includes(gradingScale)) {
    const n = Number(g);
    if (Number.isNaN(n)) return null;
    base = n >= 90 ? 4 : n >= 80 ? 3 : n >= 70 ? 2 : n >= 60 ? 1 : 0;
  } else if (gradingScale in RAW_SCALE_DIVISORS) {
    const n = Number(g);
    if (Number.isNaN(n)) return null;
    // Cap at 4.0: a 5.0 on a 0.0-5.0 scale is an A, not a 4.0-scale impossibility.
    base = Math.min(n / RAW_SCALE_DIVISORS[gradingScale], 4);
  } else {
    if (['A', 'A+', 'A-'].includes(g)) base = 4;
    else if (['B', 'B+', 'B-'].includes(g)) base = 3;
    else if (['C', 'C+', 'C-'].includes(g)) base = 2;
    else if (['D', 'D+', 'D-'].includes(g)) base = 1;
    else if (['F', 'F+', 'F-'].includes(g)) base = 0;
  }

  if (base === null) return null;
  return { unweighted: base, weighted: base + bonus };
};

/**
 * Which of the four GPAs a row counts toward, for the at-a-glance chips.
 * Mirrors the backend filterByGradeYear; if that changes, change this too.
 *
 * The A-G filter is applied below: a row the student marked Non A-G stops
 * lighting Capped and Uncapped while keeping Unweighted and Weighted. A row
 * with no A-G value recorded lights everything and shows an amber "A-G?" chip.
 */
/**
 * Why a row contributes to nothing, or null when it does contribute.
 *
 * The chips used to be computed from grade year alone, so they lit for rows the
 * calculator was silently dropping. The worst case was a whole school: a block
 * whose Schedule we could not map returned unable_to_normalize for every credit,
 * so every row at that school contributed nothing to any of the four numbers
 * while the grid showed four lit chips. High Bluff Academy is an "Other" block
 * in real data, so that was live.
 *
 * Keep this in step with isExcludedFromAllModes and computeWeightedAverage on
 * the backend. If the two disagree, the grid lies about the tiles.
 */
const rowExclusionReason = (row: RowShape): string | null => {
  if (row.gradeNo < 9) return 'Middle school coursework does not count toward any GPA.';
  if (row.excludedAsDuplicate) return 'Counted once, as the college course.';
  if (row.excludedAsTermSummary) {
    return 'Counted from the term grades above, so the final is not counted again.';
  }
  if (!isEditableGradeStatus(row.rawGrade)) {
    return 'No completed grade yet, so it does not count toward any GPA.';
  }
  const credit = Number(row.rawCredit);
  if (row.rawCredit.trim() === '' || Number.isNaN(credit) || credit <= 0) {
    return 'No usable credit value, so it does not count toward any GPA.';
  }
  return null;
};

/**
 * What to SAY about a row in the "Counts for" column, 2026-09-29.
 *
 * This column used to render four chips on every row. On a real transcript
 * that is forty rows times four chips, and on the overwhelming majority of
 * them all four were lit and said the same thing. Michelle, seeing the live
 * page: "this looks super busy."
 *
 * She is right, and the density rule in the design system already said so:
 * a marker that appears on every row carries no information. The chips were
 * costing attention on every ordinary course to communicate nothing, which
 * buried the handful of rows that genuinely need a second look.
 *
 * So the column is inverted. A course that counts toward all four says
 * nothing at all, because that is the normal case and silence reads as
 * normal. Only an exception gets a label, and the label says which number is
 * affected rather than making the reader decode four chips.
 *
 * Returns null when the row is ordinary and there is nothing to say.
 */
const rowCountsNote = (row: RowShape): { label: string; detail: string } | null => {
  const reason = rowExclusionReason(row);
  if (reason) return { label: 'Not counted', detail: reason };

  const chips = rowContributesTo(row);
  const all = chips[1] && chips[2] && chips[3] && chips[4];
  if (all) return null;

  // The only partial case the rules produce: a course that counts toward the
  // two school GPAs but not toward the two admission ones. Either it is marked
  // Non A-G, or it sits outside the 10-12 window UC and CSU use.
  if (chips[1] && chips[2] && !chips[3] && !chips[4]) {
    if (row.isAgApproved === false) {
      return {
        label: 'Not calculated into UC/CSU GPA',
        detail:
          'Marked Non A-G, so it counts toward Unweighted and Weighted but not ' +
          'toward Capped or Uncapped. A high school GPA includes PE; UC and CSU do not.',
      };
    }
    /*
      The ONLY row that reaches here is non-summer 9th grade. 10th, 11th and
      12th are all inside the window, and anything below 9th is excluded from
      every mode earlier by rowExclusionReason.

      Summer after 9th grade IS counted, so the copy must not say "9th grade
      does not count". Michelle, 2026-09-29, from the CSU counselor conference:
      CSU counts summer after 9th in its 10-12 calculation, which matches UC
      and matches what the calculator already does. See filterByGradeYear in
      gpa-calculator.ts, which admits (gradeNo === 9 && summerCourse).
    */
    return {
      label: 'Not calculated into UC/CSU GPA',
      detail:
        'UC and CSU do not count 9th grade coursework, apart from courses ' +
        'taken in the summer after 9th grade. This one counts toward ' +
        'Unweighted and Weighted only.',
    };
  }

  // Anything else is a combination the rules are not expected to produce.
  // Name the modes rather than guessing at a reason.
  const counts = ([1, 2, 3, 4] as const).filter((m) => chips[m]).map((m) => MODE_CHIP_LABEL[m]);
  return {
    label: counts.length ? `Only ${counts.join(', ')}` : 'Not counted',
    detail: counts.length
      ? `This course counts toward ${counts.join(', ')} and no other GPA.`
      : 'This course does not count toward any GPA.',
  };
};

const rowContributesTo = (row: RowShape): Record<1 | 2 | 3 | 4, boolean> => {
  if (rowExclusionReason(row) !== null) return { 1: false, 2: false, 3: false, 4: false };
  const g = row.gradeNo;
  if (row.courseType === 'COLLEGE') return { 1: true, 2: true, 3: true, 4: true };
  const inHs = g >= 9 && g <= 12;
  const inWindow = g === 10 || g === 11 || g === 12 || (g === 9 && row.summerCourse);
  // A-G, 2026-09-23. UC and CSU count only A-G coursework, so a course the
  // student marked Non A-G leaves Capped and Uncapped while staying in the two
  // school GPAs. An unknown (null) A-G status is NOT treated as Non A-G — see
  // deriveAgApproved.
  const inAdmission = inWindow && row.isAgApproved !== false;
  return { 1: inHs, 2: inHs, 3: inAdmission, 4: inAdmission };
};

/**
 * Dual-enrollment duplicate detection, 2026-09-23.
 *
 * A dual-enrollment course appears twice: once from the high school transcript
 * and once from the college transcript, at two different credit values.
 * Joanna Kruglick's Palomar `ENGL C1000` is 4.0 units at Palomar and 10.00
 * credits on her Poway High transcript. On the UC and CSU applications it is
 * entered once, as the college course.
 *
 * Deliberately conservative: we only propose a pair, never merge one. A bogus
 * prompt teaches students to dismiss prompts, and a bogus confirmed merge
 * corrupts the number. Mirrors normalization/duplicate-detection.ts on the
 * backend; keep the two in step.
 */
const LEVEL_QUALIFIERS = [
  'dual enrollment', 'dual credit', 'concurrent enrollment', 'college credit',
  'advanced placement', 'international baccalaureate', 'honors', 'advanced',
  'college', 'dual', 'hon', 'adv', 'ap', 'ib', 'de', 'cc',
];
const ROMAN_TO_ARABIC: Record<string, string> = { i: '1', ii: '2', iii: '3', iv: '4', v: '5' };

const normalizeCourseName = (raw: string): string => {
  let name = (raw ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  for (const q of LEVEL_QUALIFIERS) {
    name = name.replace(new RegExp(`(^|\\s)${q}(\\s|$)`, 'g'), ' ');
  }
  return name.split(/\s+/).filter(Boolean).map((t) => ROMAN_TO_ARABIC[t] ?? t).join(' ').trim();
};

interface DuplicatePair {
  hs: RowShape;
  college: RowShape;
}

const findDuplicatePairs = (rows: RowShape[]): DuplicatePair[] => {
  const countable = rows.filter(
    (r) => isEditableGradeStatus(r.rawGrade) && !r.excludedAsDuplicate && r.gradeNo >= 9
  );
  const hsRows = countable.filter((r) => r.courseType !== 'COLLEGE');
  const collegeRows = countable.filter((r) => r.courseType === 'COLLEGE');
  if (!hsRows.length || !collegeRows.length) return [];

  const pairs: DuplicatePair[] = [];
  const claimed = new Set<number>();
  for (const hs of hsRows) {
    const hsName = normalizeCourseName(hs.course);
    if (!hsName) continue;
    for (const college of collegeRows) {
      if (claimed.has(college.detailId) || claimed.has(hs.detailId)) continue;
      if (normalizeCourseName(college.course) !== hsName) continue;
      claimed.add(hs.detailId);
      claimed.add(college.detailId);
      pairs.push({ hs, college });
      break;
    }
  }
  return pairs;
};

const nextSeqNo = (rows: RowShape[], gradeNo: number): number => {
  const inGrade = rows.filter((r) => r.gradeNo === gradeNo);
  if (!inGrade.length) return 1;
  return Math.max(...inGrade.map((r) => Number(r.seqNo) || 0)) + 1;
};

// ---------- data ----------

const useForecasterRows = (studentId: string | number | null | undefined) => {
  const [rows, setRows] = React.useState<RowShape[]>([]);
  const [blocks, setBlocks] = React.useState<BlockShape[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [reloadKey, setReloadKey] = React.useState(0);

  const reload = React.useCallback(() => setReloadKey((k) => k + 1), []);

  React.useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const gradeNos = [9, 10, 11, 12, 13];
        const perYear = await Promise.all(
          gradeNos.map((gradeNo) =>
            gpaForecastServices.getGpaForeCastByGradeNo(studentId, gradeNo).catch(() => [])
          )
        );
        const allForecasts: any[] = perYear.flat();
        const detailsPerForecast = await Promise.all(
          allForecasts.map((f: any) =>
            gpaForecastDetailServices.getGpaForeCastDetails(f.gpa_forecast_id).catch(() => [])
          )
        );
        if (cancelled) return;

        const blockList: BlockShape[] = allForecasts.map((f: any) => ({
          gpaForecastId: f.gpa_forecast_id,
          studentId: f.student_id,
          seqNo: f.seq_no,
          gradeNo: f.grade_no,
          schoolName: f.school_name || '',
          schoolCode: f.school_code || '',
          schedule: f.schedule || '',
          gradingScale: f.grading_scale || 'A-F',
          creditsAreAnnual: f.credits_are_annual === true,
        }));

        const flat: RowShape[] = [];
        allForecasts.forEach((f: any, i: number) => {
          const details = detailsPerForecast[i] ?? [];
          for (const d of details) {
            flat.push({
              gpaForecastId: f.gpa_forecast_id,
              studentId: f.student_id,
              seqNo: d.seq_no ?? f.seq_no,
              gradeNo: f.grade_no,
              schoolName: f.school_name || d.school_name || '',
              schoolCode: f.school_code || '',
              schedule: f.schedule || '',
              gradingScale: f.grading_scale || 'A-F',
              courseType: f.grade_no === 13 ? 'COLLEGE' : 'HS',
              detailId: d.id,
              course: d.course || '',
              term: d.term || '',
              rawGrade: d.grade ?? '',
              rawCredit: String(d.credit ?? ''),
              isWeightPoint: !!d.is_weight_point,
              summerCourse: !!d.summer_course,
              unweightedPoint: d.unweighted_point === null || d.unweighted_point === undefined ? '' : String(d.unweighted_point),
              weightedPoint: d.weighted_point === null || d.weighted_point === undefined ? '' : String(d.weighted_point),
              isCustom: !!d.is_custom,
              isOverride: !!d.is_override,
              agSubjectArea: (d.ag_subject_area ?? '').toString().trim() || null,
              typeOfClass: (d.type_of_class ?? '').toString().trim() || null,
              isAgApproved: deriveAgApproved(d.ag_subject_area, f.grade_no),
              excludedAsDuplicate: d.excluded_as_duplicate === true,
              creditsAreAnnual: f.credits_are_annual === true,
              scheduleUnrecognised: !KNOWN_SCHEDULES.includes((f.schedule || '').trim()),
              // Set by markTermSummaries below, once every row is loaded and
              // the shape of each course is known.
              excludedAsTermSummary: false,
              dirty: false,
              saving: false,
            });
          }
        });
        setBlocks(blockList);
        setRows(markTermSummaries(flat));
      } catch (ex) {
        console.log('GpaForecasterV2: failed to load rows', ex);
        setRows([]);
        setBlocks([]);
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [studentId, reloadKey]);

  return { rows, setRows, blocks, loading, reload };
};

// ---------- row ----------

const CourseRowView: React.FC<{
  row: RowShape;
  onChange: (partial: Partial<RowShape>) => void;
  onSave: () => void;
  /** Save a row we already hold, bypassing the state round trip. See the toggle. */
  onSaveNow: (row: RowShape) => void;
  onUndoDuplicate: () => void;
  onDelete: () => void;
}> = ({ row, onChange, onSave, onSaveNow, onUndoDuplicate, onDelete }) => {
  const countsNote = rowCountsNote(row);
  const isExcluded = !isEditableGradeStatus(row.rawGrade);
  const isCollege = row.courseType === 'COLLEGE';
  const gradeOptions = React.useMemo(() => gradeOptionsFor(row.gradingScale), [row.gradingScale]);
  const freeTextGrade = gradeOptions.length === 0;
  const creditInvalid = row.rawCredit.trim() === '' || Number.isNaN(Number(row.rawCredit));

  /**
   * FIX H2 — a grade or weighted change repoints the row, unless a human has
   * overridden the points. Without this the row keeps its old points beside a
   * new grade, and the legacy page and counselor view read those columns.
   */
  const repoint = (partial: Partial<RowShape>) => {
    const nextGrade = partial.rawGrade ?? row.rawGrade;
    // A college course is always weighted, so its prefilled points must carry
    // the bonus whatever is_weight_point happens to hold on the row. Otherwise
    // changing the grade on a college row would write unweighted points beside
    // a backend that is bonusing it.
    const nextWeighted = isCollege ? true : partial.isWeightPoint ?? row.isWeightPoint;
    if (row.isOverride) {
      onChange({ ...partial, dirty: true });
      return;
    }
    const pts = prefillPoints(row.gradingScale, nextGrade, nextWeighted);
    onChange({
      ...partial,
      dirty: true,
      unweightedPoint: pts ? String(pts.unweighted) : '',
      weightedPoint: pts ? String(pts.weighted) : '',
    });
  };

  return (
    <tr style={rowStyle(isCollege ? 'college' : isExcluded ? 'excluded' : 'default')}>
      <td style={{ ...tdStyle, minWidth: '190px' }}>
        <span style={{ color: T.ink, fontWeight: 500 }}>
          {row.course || <em style={{ color: T.inkMuted }}>Untitled</em>}
        </span>
        {isCollege ? (
          <span
            style={{
              ...chipInline({ color: T.accentInk, bg: T.accentTint }),
              // Usersnap #2763: same size as the "Yes" weight toggle and on its
              // own line under the course name, like the A-G picker.
              display: 'block',
              width: 'fit-content',
              marginLeft: 0,
              marginTop: '4px',
              padding: `3px ${T.space3}`,
            }}
          >
            college
          </span>
        ) : null}
        {/*
          A-G picker, 2026-09-23. Three states, because "we do not know" is not
          the same as "not A-G" and a counselor needs to tell them apart.
          Editable rather than read-only: a manually added course never appears
          in grade_N_course_array, so no sync will ever fill this in and telling
          the user to refresh would be advice they cannot follow.
        */}
        {!isCollege ? (
          <select
            value={row.agSubjectArea ?? ''}
            onChange={(e) => {
              const next = e.target.value || null;
              onSaveNow({ ...row, agSubjectArea: next, dirty: true });
              onChange({ agSubjectArea: next, dirty: true });
            }}
            style={{
              ...chipInline(
                row.isAgApproved === true
                  ? { color: T.successInk, bg: T.successWash, size: 'xs' }
                  : row.isAgApproved === false
                  ? // Usersnap #2763: an excluded row is already filled with T.sunken,
                    // so the Non A-G pill switches to white there to stay visible.
                    { color: T.inkMuted, bg: isExcluded ? T.surface : T.sunken, size: 'xs' }
                  : { color: T.warningInk, bg: T.warningWash, size: 'xs' }
              ),
              // Usersnap #2762: always sit on its own line under the course name.
              // Usersnap #2763: back to the full pill radius used by other chips.
              display: 'block',
              marginLeft: 0,
              marginTop: '4px',
              borderRadius: '999px',
              // App.css sets every <select> to 48px. Usersnap #2763: one compact
              // size for every A-G state so all rows render the same pill.
              height: '23.5px',
              minHeight: '23.5px',
              padding: '1px 7px',
              // ClickUp 86ak9ab4g: every state gets a visible 1px edge one step
              // darker than its fill, so each chip has a distinct boundary and
              // all pills keep the same outer size.
              borderStyle: 'solid',
              borderWidth: '1px',
              borderColor:
                row.isAgApproved === true
                  ? T.successEdge
                  : row.isAgApproved === false
                  ? T.hairlineStrong
                  : T.warningEdge,
              cursor: 'pointer',
              // Usersnap #2763: cap the pill at 180px so it does not stretch to the
              // full cell width; long areas ("E Language Other than English") end
              // with an ellipsis and stay readable via the native dropdown list.
              // overflow: hidden is omitted because Chrome ignores it on <select>.
              maxWidth: '180px',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontFamily: T.fontUi,
            }}
            title={
              row.isAgApproved === true
                ? `A-G approved: ${formatAgAreaLabel(row.agSubjectArea)}`
                : row.isAgApproved === false
                ? 'Marked Non A-G. Counts toward the school GPAs but not toward UC or CSU.'
                : 'No A-G subject area recorded. Counted in every GPA for now. Pick one to make it exact.'
            }
          >
            <option value="">A-G?</option>
            {/* Value stays the stored string; only the visible text is formatted. */}
            {subjectAreaOptions.map((area: string) => (
              <option key={area} value={area}>
                {formatAgAreaLabel(area)}
              </option>
            ))}
          </select>
        ) : null}
        {row.isCustom ? (
          <span style={chipInline({ color: T.inkMuted, bg: T.nested, border: T.hairline, size: 'xs' })}>added</span>
        ) : null}
        {row.excludedAsDuplicate ? (
          <button
            onClick={onUndoDuplicate}
            style={{
              ...chipInline({ color: T.warningInk, bg: T.warningWash, size: 'xs' }),
              border: 'none',
              cursor: 'pointer',
              fontFamily: T.fontUi,
            }}
            title="Counted once, as the college course. Click to count this row again."
          >
            merged · undo
          </button>
        ) : null}
        {/*
          D6. An excluded row used to carry a third marker here, a chip
          repeating the grade. The row already reads as excluded from its
          sunken fill and says why in the "Counts for" column, and the Grade
          column shows the grade itself. Two markers is the limit.
        */}
      </td>

      {/*
        Weighted course toggle, sitting immediately right of the course name
        (Michelle, 2026-09-23). The star that used to lead the row has been
        removed: with an explicit Yes/No here it was a second marker of the same
        state, which the design rules disallow.
      */}
      <td style={{ ...tdStyle, width: '86px', textAlign: 'center' }}>
        {isCollege ? (
          /*
            A college course is always weighted. Michelle, 2026-09-24: "if it's
            a college course, it's weighted." The backend labels every grade-13
            row CL in course-row-adapter.ts, before the Type of Class map and
            regardless of is_weight_point, and CL earns the bonus in Weighted,
            Capped and Uncapped for every student. An editable toggle here could
            say "No" on a row the calculator was bonusing, so it is shown as a
            fact rather than offered as a choice.
          */
          <span
            style={{
              display: 'inline-block',
              padding: `3px ${T.space3}`,
              fontSize: T.textXs,
              fontWeight: T.weightSemibold,
              borderRadius: T.radiusControl,
              background: T.accentTint,
              color: T.accentInk,
            }}
            title="College coursework is always weighted."
          >
            Yes
          </span>
        ) : (
        <button
          onClick={() => {
            // Compute the next row here and hand it straight to the save, rather
            // than mutating state and hoping a later closure sees it. The old
            // setTimeout(onSave) captured a render where `dirty` was still false,
            // so the save guard returned early and the toggle never persisted.
            const nextWeighted = !row.isWeightPoint;
            const pts = row.isOverride
              ? null
              : prefillPoints(row.gradingScale, row.rawGrade, nextWeighted);
            const patch: Partial<RowShape> = {
              isWeightPoint: nextWeighted,
              dirty: true,
              ...(pts
                ? { unweightedPoint: String(pts.unweighted), weightedPoint: String(pts.weighted) }
                : {}),
            };
            onChange(patch);
            onSaveNow({ ...row, ...patch });
          }}
          style={{
            padding: `3px ${T.space3}`,
            fontSize: T.textXs,
            fontWeight: T.weightSemibold,
            cursor: 'pointer',
            background: row.isWeightPoint ? T.accentTint : T.sunken,
            color: row.isWeightPoint ? T.accentInk : T.inkMuted,
            border: 'none',
            borderRadius: T.radiusControl,
            fontFamily: T.fontUi,
          }}
          title={row.isWeightPoint ? 'This course is weighted' : 'This course is not weighted'}
        >
          {row.isWeightPoint ? 'Yes' : 'No'}
        </button>
        )}
      </td>

      <td style={{ ...tdStyle, minWidth: '120px', color: T.inkMuted, fontSize: T.textXs }}>
        {row.schoolName}
        {row.gradingScale && row.gradingScale !== 'A-F' ? (
          <span style={chipInline({ color: T.inkMuted, bg: T.nested, border: T.hairline, size: 'xs' })}>
            {row.gradingScale}
          </span>
        ) : null}
        {row.scheduleUnrecognised ? (
          <span
            style={chipInline({ color: T.warningInk, bg: T.warningWash, size: 'xs' })}
            title="We do not recognise this school's term system, so the credit is counted at face value and the weighting is approximate. Set the school's Schedule in Courses & Grades to make it exact."
          >
            term system?
          </span>
        ) : null}
      </td>

      <td style={{ ...tdStyle, minWidth: '80px', fontSize: T.textXs }}>
        {termLabel(row.schedule, row.gradeNo, row.term)}
      </td>

      {/* Grade — FIX B2, options from the block's grading scale */}
      <td style={{ ...tdStyle, width: '96px', textAlign: 'center' }}>
        {freeTextGrade ? (
          // "Other" scale: free text, matching how Courses & Grades collects it.
          <input
            type="text"
            value={row.rawGrade}
            onChange={(e) => repoint({ rawGrade: e.target.value })}
            onBlur={onSave}
            style={{ ...inputStyle, ...inputCenterStyle }}
            aria-label="Grade"
          />
        ) : (
          <select
            value={row.rawGrade}
            onChange={(e) => repoint({ rawGrade: e.target.value })}
            onBlur={onSave}
            style={{ ...inputStyle, ...inputCenterStyle }}
          >
            <option value=""></option>
            {/* Keep an unrecognised stored value selectable so it is never silently replaced. */}
            {row.rawGrade && !gradeOptions.includes(row.rawGrade) ? (
              <option value={row.rawGrade}>{row.rawGrade}</option>
            ) : null}
            {gradeOptions.map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            ))}
          </select>
        )}
      </td>

      {/* Credit — FIX B3, string state, validated before save */}
      <td style={{ ...tdStyle, width: '76px', textAlign: 'center' }}>
        <input
          type="text"
          inputMode="decimal"
          value={row.rawCredit}
          onChange={(e) => onChange({ rawCredit: e.target.value, dirty: true })}
          onBlur={onSave}
          style={{ ...(creditInvalid ? invalidInputStyle : inputStyle), ...inputCenterStyle }}
          aria-label="Course credits"
        />
        {creditInvalid ? (
          <div style={{ fontSize: T.textXs, color: T.dangerInk, marginTop: '2px' }}>
            Enter a number
          </div>
        ) : null}
      </td>

      {/* Points — FIX H1, restored from the legacy page */}
      <td style={{ ...tdStyle, width: '74px', textAlign: 'center' }}>
        <input
          type="text"
          inputMode="decimal"
          value={row.unweightedPoint}
          onChange={(e) => onChange({ unweightedPoint: e.target.value, isOverride: true, dirty: true })}
          onBlur={onSave}
          style={{ ...inputStyle, ...inputCenterStyle }}
          aria-label="Unweighted grade points"
        />
      </td>
      <td style={{ ...tdStyle, width: '74px', textAlign: 'center' }}>
        {row.isOverride ? (
          // Usersnap #2763: invisible spacer that mirrors the "edited" line below,
          // so the input stays vertically centered in the row.
          <div
            aria-hidden="true"
            style={{
              fontSize: T.textXs,
              color: 'transparent',
              // Color emoji fonts can ignore `color`; visibility keeps it hidden.
              visibility: 'hidden',
              userSelect: 'none',
              marginBottom: '2px',
            }}
          >
            &#x1F58B;
          </div>
        ) : null}
        <input
          type="text"
          inputMode="decimal"
          value={row.weightedPoint}
          onChange={(e) => onChange({ weightedPoint: e.target.value, isOverride: true, dirty: true })}
          onBlur={onSave}
          style={{ ...inputStyle, ...inputCenterStyle }}
          aria-label="Weighted grade points"
        />
        {row.isOverride ? (
          <div style={{ fontSize: T.textXs, color: T.accentInk, marginTop: '2px' }} title="These points were set by hand and will not be recalculated">
            edited
          </div>
        ) : null}
      </td>

      <td style={{ ...tdStyle, minWidth: '250px' }}>
        {countsNote ? (
          <span
            style={chipInline({
              color: T.warningInk,
              bg: T.warningWash,
              size: 'xs',
            })}
            title={countsNote.detail}
          >
            {countsNote.label}
          </span>
        ) : null}
        {row.saving ? (
          <span style={{ fontSize: T.textXs, color: T.inkMuted, marginLeft: T.space1 }}>saving…</span>
        ) : row.dirty ? (
          <span style={{ fontSize: T.textXs, color: T.warningInk, marginLeft: T.space1 }}>unsaved</span>
        ) : null}
      </td>

      {/* Delete — FIX B4, manually-added rows only */}
      <td style={{ ...tdStyle, width: '30px', textAlign: 'center' }}>
        {row.isCustom ? (
          <button
            onClick={onDelete}
            style={{
              border: 'none',
              background: 'transparent',
              color: T.inkMuted,
              cursor: 'pointer',
              fontSize: T.textMd,
              padding: 0,
            }}
            title="Delete this added course"
            aria-label="Delete this added course"
          >
            ✕
          </button>
        ) : null}
      </td>
    </tr>
  );
};

// ---------- add course ----------

/**
 * FIX B1 + H4 — the course is attached to an EXISTING coursework block the user
 * picks, so the payload carries a real gpa_forecast_id and the flat shape the
 * CreateGpaForecastDetailDto expects. The legacy page worked this way.
 *
 * If a grade year has no block yet, there is nothing to attach to and the user
 * is sent to Courses & Grades to add the school first. Creating a parent
 * forecast row from here would fragment the student's transcript into one
 * school record per course.
 */
const AddCourseModal: React.FC<{
  gradeNo: number;
  studentId: any;
  blocks: BlockShape[];
  seqNo: number;
  onClose: () => void;
  onAdded: () => void;
}> = ({ gradeNo, studentId, blocks, seqNo, onClose, onAdded }) => {
  const options = blocks.filter((b) => b.gradeNo === gradeNo);
  const [blockId, setBlockId] = React.useState<number | null>(options[0]?.gpaForecastId ?? null);
  const block = options.find((b) => b.gpaForecastId === blockId) ?? null;

  const [courseName, setCourseName] = React.useState('');
  const [term, setTerm] = React.useState('');
  const [grade, setGrade] = React.useState('');
  const [credit, setCredit] = React.useState('');
  const [isWeightPoint, setIsWeightPoint] = React.useState(false);
  const [agSubjectArea, setAgSubjectArea] = React.useState('');
  const [summerCourse, setSummerCourse] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const gradeOptions = block ? gradeOptionsFor(block.gradingScale) : [];
  const termOptions = block ? termOptionsFor(block.schedule, gradeNo) : [];

  React.useEffect(() => {
    // Reset when the current term is not offered by the newly selected block.
    // The old `!term` guard meant switching from a Quarter block to a Semester
    // one kept "Quarter 3", which then saved into a Semester block and never
    // matched its term options again.
    if (!termOptions.length) return;
    if (!term || !termOptions.includes(term)) setTerm(termOptions[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blockId]);

  const creditNum = Number(credit);
  const creditValid = credit.trim() !== '' && !Number.isNaN(creditNum) && creditNum >= 0;
  const canSave = !!block && courseName.trim() !== '' && term.trim() !== '' && creditValid;

  const handleSave = async () => {
    if (!canSave || !block) {
      setError('Fill in the course name, term and a numeric credit value.');
      return;
    }
    setSaving(true);
    setError(null);
    const pts = prefillPoints(block.gradingScale, grade, isWeightPoint);
    try {
      // Flat body matching CreateGpaForecastDetailDto. Note the DTO field is
      // `credits`, plural; `credit` is sent as well because the entity column is
      // singular and the DTO is not validated with forbidNonWhitelisted.
      await gpaForecastDetailServices.createGpaForeCastDetail({
        gpa_forecast_id: block.gpaForecastId,
        student_id: Number(studentId ?? block.studentId ?? 0),
        is_custom: true,
        is_override: false,
        seq_no: seqNo,
        school_name: block.schoolName,
        summer_course: summerCourse,
        course: courseName.trim(),
        term: term.trim(),
        grade,
        credit: creditNum,
        credits: creditNum,
        is_weight_point: isWeightPoint,
        unweighted_point: pts ? pts.unweighted : 0,
        weighted_point: pts ? pts.weighted : 0,
        // Collected here because a manually added course never appears in
        // grade_N_course_array, so no sync will ever fill it in.
        ag_subject_area: agSubjectArea || null,
      });
      onAdded();
      onClose();
    } catch (ex) {
      console.log('AddCourseModal: create failed', ex);
      setError('Could not add the course. Try again, or add it in Courses & Grades.');
    }
    setSaving(false);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: T.scrim,
        zIndex: 2000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: T.fontUi,
        padding: T.space4,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: T.surface,
          borderRadius: T.radiusSurface,
          padding: T.space5,
          maxWidth: '560px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: T.elevOverlay,
          color: T.ink,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: T.space3 }}>
          <h3 style={{ margin: 0, fontSize: T.textLg, fontWeight: T.weightBold, letterSpacing: '-0.015em', color: T.ink }}>Add course to {GRADE_LABEL[gradeNo]}</h3>
          <button
            onClick={onClose}
            style={{ border: 'none', background: 'none', fontSize: T.textXl, cursor: 'pointer', color: T.inkMuted, lineHeight: 1 }}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {options.length === 0 ? (
          <div style={{ fontSize: T.textSm, color: T.inkBody, lineHeight: 1.6 }}>
            There is no school set up for {GRADE_LABEL[gradeNo]} yet. Add the school and its term
            system in Courses &amp; Grades first, then come back here to add individual courses.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: T.space3 }}>
            <div>
              <label style={labelStyle}>School</label>
              <select
                value={blockId ?? ''}
                onChange={(e) => setBlockId(Number(e.target.value))}
                style={inputStyle}
              >
                {options.map((b) => (
                  <option key={b.gpaForecastId} value={b.gpaForecastId}>
                    {b.schoolName} · {b.schedule || 'schedule not set'} · {b.gradingScale}
                  </option>
                ))}
              </select>
              <div style={{ fontSize: T.textXs, color: T.inkMuted, marginTop: '3px' }}>
                Grade and term options follow this school's grading scale and term system.
              </div>
            </div>

            <div>
              <label style={labelStyle}>A-G subject area</label>
              <select
                value={agSubjectArea}
                onChange={(e) => setAgSubjectArea(e.target.value)}
                style={inputStyle}
              >
                <option value="">Not sure yet</option>
                {/* Same display format as the row chip; stored value unchanged. */}
                {subjectAreaOptions.map((area: string) => (
                  <option key={area} value={area}>
                    {formatAgAreaLabel(area)}
                  </option>
                ))}
              </select>
              <div style={{ fontSize: T.textXs, color: T.inkMuted, marginTop: '3px' }}>
                UC and CSU count only A-G coursework. Leave this unset and the course counts
                toward every GPA until someone picks one.
              </div>
            </div>

            <div>
              <label style={labelStyle}>Course name</label>
              <input
                autoFocus
                value={courseName}
                onChange={(e) => setCourseName(e.target.value)}
                placeholder="e.g. AP Chemistry"
                style={inputStyle}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: T.space2 }}>
              <div>
                <label style={labelStyle}>Term</label>
                <select value={term} onChange={(e) => setTerm(e.target.value)} style={inputStyle}>
                  {termOptions.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>Grade</label>
                {gradeOptions.length === 0 ? (
                  <input
                    type="text"
                    value={grade}
                    onChange={(e) => setGrade(e.target.value)}
                    placeholder="Type the grade"
                    style={inputStyle}
                  />
                ) : (
                  <select value={grade} onChange={(e) => setGrade(e.target.value)} style={inputStyle}>
                    <option value=""></option>
                    {gradeOptions.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div>
                <label style={labelStyle}>Course credits</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={credit}
                  onChange={(e) => setCredit(e.target.value)}
                  placeholder={block ? 'e.g. 5' : ''}
                  style={credit.trim() !== '' && !creditValid ? invalidInputStyle : inputStyle}
                />
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: T.space2 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: T.space2, fontSize: T.textSm, color: T.inkStrong, fontWeight: 500 }}>
                <input type="checkbox" checked={isWeightPoint} onChange={(e) => setIsWeightPoint(e.target.checked)} />
                Weighted course (AP / IB / Honors / college)
              </label>
              {gradeNo === 9 ? (
                <label style={{ display: 'flex', alignItems: 'center', gap: T.space2, fontSize: T.textSm, color: T.inkStrong, fontWeight: 500 }}>
                  <input type="checkbox" checked={summerCourse} onChange={(e) => setSummerCourse(e.target.checked)} />
                  Summer course taken after 9th grade
                  <span style={{ fontWeight: 400, color: T.inkMuted, fontSize: T.textXs }}>
                    (this is what lets a 9th grade course count toward the UC numbers)
                  </span>
                </label>
              ) : null}
            </div>

            {error ? (
              <div style={{ fontSize: T.textSm, color: T.dangerInk }}>{error}</div>
            ) : null}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: T.space2, marginTop: T.space4 }}>
          <button onClick={onClose} style={secondaryBtnStyle}>
            Cancel
          </button>
          {options.length > 0 ? (
            <button
              onClick={handleSave}
              disabled={!canSave || saving}
              style={{ ...addBtnStyle, opacity: canSave && !saving ? 1 : 0.5 }}
            >
              {saving ? 'Adding…' : 'Add course'}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// ---------- page ----------

const GpaForecasterV2 = ({ studentId }: Props) => {
  const { rows, setRows, blocks, loading, reload } = useForecasterRows(studentId);
  const [addForGradeNo, setAddForGradeNo] = React.useState<number | null>(null);
  /** FIX H5 — bumping this remounts the hero card so the tiles follow an edit. */
  const [heroKey, setHeroKey] = React.useState(0);
  const [syncing, setSyncing] = React.useState(false);
  const [dismissedPairs, setDismissedPairs] = React.useState<number[]>([]);

  const handleRowChange = (detailId: number, partial: Partial<RowShape>) => {
    setRows((prev) => prev.map((r) => (r.detailId === detailId ? { ...r, ...partial } : r)));
  };

  /**
   * Persist a row we already hold. Split out of handleRowSave so a control that
   * computes the next row itself (the weighted toggle) can save it directly
   * rather than relying on a closure that captured the pre-change state.
   */
  const persistRow = async (row: RowShape) => {
    const detailId = row.detailId;

    /**
     * FIX B3 — never write a blank or non-numeric credit.
     *
     * This used to `return` outright, which silently discarded any OTHER change
     * on the row: picking an A-G area or flipping the weighted toggle on a row
     * whose credit box was empty updated local state, showed "unsaved", posted
     * nothing, and lost the change on reload. Those are exactly the rows the
     * "No usable credit value" message asks the user to fix.
     *
     * Now the credit is simply omitted from the payload and everything else is
     * saved. The backend merges, so the stored credit is left alone.
     */
    const creditNum = Number(row.rawCredit);
    const creditValid = row.rawCredit.trim() !== '' && !Number.isNaN(creditNum);

    const unweighted = Number(row.unweightedPoint);
    const weighted = Number(row.weightedPoint);
    const pointsValid =
      row.unweightedPoint.trim() !== '' &&
      row.weightedPoint.trim() !== '' &&
      !Number.isNaN(unweighted) &&
      !Number.isNaN(weighted);

    handleRowChange(detailId, { saving: true });
    try {
      await gpaForecastDetailServices.updateGpaForeCastDetail({
        id: row.detailId,
        gpa_forecast_id: row.gpaForecastId,
        student_id: row.studentId,
        // FIX H1 — is_custom / is_override / seq_no are round-tripped rather than
        // dropped. The backend uses TypeORM merge so omitting them would not lose
        // them, but sending them keeps this payload honest and self-describing.
        is_custom: row.isCustom,
        is_override: row.isOverride,
        seq_no: row.seqNo,
        course: row.course,
        term: row.term,
        grade: row.rawGrade,
        ...(creditValid ? { credit: creditNum } : {}),
        is_weight_point: row.isWeightPoint,
        summer_course: row.summerCourse,
        school_name: row.schoolName,
        ag_subject_area: row.agSubjectArea,
        ...(pointsValid ? { unweighted_point: unweighted, weighted_point: weighted } : {}),
      });
      handleRowChange(detailId, {
        dirty: false,
        saving: false,
        // Recompute locally so the chip and the "counts for" cell update without
        // waiting for a reload.
        isAgApproved: deriveAgApproved(row.agSubjectArea, row.gradeNo),
      });
      setHeroKey((k) => k + 1);
    } catch (ex) {
      console.log('GpaForecasterV2: row save failed', ex);
      handleRowChange(detailId, { saving: false });
      alert('Could not save that change. Please try again.');
    }
  };

  const handleRowSave = async (detailId: number) => {
    const row = rows.find((r) => r.detailId === detailId);
    if (!row || !row.dirty || row.saving) return;
    await persistRow(row);
  };

  const handleRowDelete = async (detailId: number) => {
    const row = rows.find((r) => r.detailId === detailId);
    if (!row) return;
    if (!row.isCustom) return; // FIX B4 — belt and braces; the control is hidden too.
    if (!window.confirm(`Delete "${row.course}"? This only removes the course you added here.`)) return;
    try {
      await gpaForecastDetailServices.deleteGpaForeCastDetail(row.detailId);
      setRows((prev) => prev.filter((r) => r.detailId !== detailId));
      setHeroKey((k) => k + 1);
    } catch (ex) {
      console.log('GpaForecasterV2: delete failed', ex);
      alert('Could not delete that row.');
    }
  };

  /**
   * Confirm a dual-enrollment pair. Flags the high-school side so the course
   * counts once, as the college entry. Nothing is deleted; unsetting the flag
   * restores it.
   */
  /**
   * Confirm a proposed pair. Flags EVERY term row of the high-school course, not
   * just the one that matched. Joanna Kruglick's Poway `ENGL C1000` has a
   * Semester 1 and a Semester 2 row; flagging one would leave half the credit
   * still double-counted while the prompt disappeared, which is worse than not
   * asking.
   */
  const setDuplicateForCourse = async (anyRowOfCourse: RowShape, excluded: boolean) => {
    const siblings = rows.filter(
      (r) =>
        r.courseType !== 'COLLEGE' &&
        r.gpaForecastId === anyRowOfCourse.gpaForecastId &&
        r.seqNo === anyRowOfCourse.seqNo &&
        r.course.trim().toLowerCase() === anyRowOfCourse.course.trim().toLowerCase()
    );
    for (const sib of siblings) {
      await handleSetDuplicate(sib.detailId, excluded);
    }
  };

  const confirmPair = (pair: DuplicatePair) => setDuplicateForCourse(pair.hs, true);

  const handleSetDuplicate = async (hsDetailId: number, excluded: boolean) => {
    const hs = rows.find((r) => r.detailId === hsDetailId);
    if (!hs) return;
    try {
      await gpaForecastDetailServices.updateGpaForeCastDetail({
        id: hs.detailId,
        excluded_as_duplicate: excluded,
      });
      handleRowChange(hsDetailId, { excludedAsDuplicate: excluded });
      setHeroKey((k) => k + 1);
    } catch (ex) {
      console.log('GpaForecasterV2: duplicate flag update failed', ex);
      alert(`Could not update "${hs.course}". Please try again.`);
    }
  };

  const handleDismissDuplicate = (hsDetailId: number) => {
    setDismissedPairs((prev) => [...prev, hsDetailId]);
  };

  /**
   * Toggle the annual-credit flag on a coursework block. When on, the credit
   * printed on the transcript is the whole-year figure and the calculator
   * spreads it across the course's terms rather than counting it per term.
   */
  const handleToggleAnnualCredits = async (block: BlockShape) => {
    const next = !block.creditsAreAnnual;
    try {
      /**
       * The endpoint looks the row up by (student_id, seq_no, grade_no), NOT by
       * gpa_forecast_id — see updateByStudent in gpa-forecast.service.ts. It
       * also uses body.student_id for the ownership check. So those three must
       * be sent or the call either fails the ownership check or, worse, matches
       * an arbitrary row and merges this body over it.
       *
       * We still omit school_name, schedule and grading_scale: the merge would
       * write them, and they are not what we are changing.
       */
      await gpaForecastServices.updateGpaForeCast({
        gpa_forecast_id: block.gpaForecastId,
        student_id: block.studentId,
        seq_no: block.seqNo,
        grade_no: block.gradeNo,
        credits_are_annual: next,
      });
      reload();
      setHeroKey((k) => k + 1);
    } catch (ex) {
      console.log('GpaForecasterV2: annual-credit toggle failed', ex);
      alert('Could not save that setting. Please try again.');
    }
  };

  /** FIX B5 — pull newly-entered coursework across from Courses & Grades. */
  const handleSync = async () => {
    if (!studentId || syncing) return;
    setSyncing(true);
    try {
      await gpaForecastServices.syncGpaForecast(studentId);
      reload();
      setHeroKey((k) => k + 1);
    } catch (ex) {
      console.log('GpaForecasterV2: sync failed', ex);
      alert('Could not refresh from Courses & Grades. Please try again.');
    }
    setSyncing(false);
  };

  const rowsByGrade: Record<number, RowShape[]> = {};
  for (const r of rows) {
    if (!rowsByGrade[r.gradeNo]) rowsByGrade[r.gradeNo] = [];
    rowsByGrade[r.gradeNo].push(r);
  }

  const gradesToRender: number[] = [...GRADE_YEARS, 13];

  const blocksForGrade = (grade: number) => blocks.filter((b) => b.gradeNo === grade);

  const duplicatePairs = React.useMemo(
    () => findDuplicatePairs(rows).filter((p) => !dismissedPairs.includes(p.hs.detailId)),
    [rows, dismissedPairs]
  );

  /**
   * Year GPA, 2026-09-24.
   *
   * This weights each row by its share of a FULL YEAR at its own block, not by
   * the raw credit on the transcript. Credit is school-relative: 5 at Del Norte
   * and 0.5 at The Bishop's School are both one semester of one course. A grade
   * year can legitimately hold two schools, which is exactly when raw credit
   * goes wrong, and a transfer student would otherwise see their old school's
   * courses weighing ten times their new school's in this one line.
   *
   * It mirrors the backend rule in school-credit-calibration.ts: the anchor is
   * the most common full-year credit total among that block's own courses, and
   *
   *   weight = row credit / that block's full-year credit
   *
   * The x2 the backend applies is left off here because it cancels out of an
   * average. When a block has no readable anchor the raw credit is used, which
   * is what the backend falls back to as well.
   *
   * This is a display convenience. The hero card is the authoritative number
   * and comes from the backend.
   */
  const blockFullYearCredit = (list: RowShape[]): Map<number, number> => {
    const annualByCourse = new Map<string, { block: number; credit: number }>();
    for (const r of list) {
      const c = Number(r.rawCredit);
      if (Number.isNaN(c) || c <= 0) continue;
      const key = `${r.gpaForecastId}::${r.seqNo}::${r.course.trim().toLowerCase()}`;
      const prev = annualByCourse.get(key);
      annualByCourse.set(key, {
        block: r.gpaForecastId,
        credit: (prev?.credit ?? 0) + c,
      });
    }

    const countsByBlock = new Map<number, Map<number, number>>();
    annualByCourse.forEach(({ block, credit }) => {
      const counts = countsByBlock.get(block) ?? new Map<number, number>();
      counts.set(credit, (counts.get(credit) ?? 0) + 1);
      countsByBlock.set(block, counts);
    });

    const anchors = new Map<number, number>();
    countsByBlock.forEach((counts, block) => {
      let best = 0;
      let bestCount = 0;
      counts.forEach((count, credit) => {
        // Ties break toward the larger total, because the anchor is a full year
        // and a tie against a half-year value should resolve to the year.
        if (count > bestCount || (count === bestCount && credit > best)) {
          best = credit;
          bestCount = count;
        }
      });
      if (best > 0) anchors.set(block, best);
    });

    return anchors;
  };

  const yearSubtotal = (grade: number): { gpa: number | null; count: number } => {
    const list = rowsByGrade[grade] ?? [];
    const usable = list.filter((r) => isEditableGradeStatus(r.rawGrade));
    if (usable.length === 0) return { gpa: null, count: 0 };

    const anchors = blockFullYearCredit(list.filter((r) => r.courseType !== 'COLLEGE'));

    /*
      College credits and high school subject credits are two different
      buckets and are never mixed. Michelle, 2026-09-24: "college credits
      might be 4.0 but the dual enrollment course at the high school could be
      5 semester credits each semester (total of 10 for the whole year. it's
      important to distinguish high school subject credits from college
      credits that are earned. two different buckets."

      So a college row is not measured against any anchor. One college
      semester is one full high school year, whatever its unit count, which is
      a weight of 1 here against a high school semester's 0.5. This mirrors
      COLLEGE_YEAR_EQUIVALENT_UNITS in course-row-adapter.ts, halved because
      the backend's x2 cancels out of an average.
    */
    const collegeTerms = new Map<string, number>();
    for (const r of list) {
      if (r.courseType !== 'COLLEGE') continue;
      const k = `${r.gpaForecastId}::${r.seqNo}::${r.course.trim().toLowerCase()}`;
      collegeTerms.set(k, (collegeTerms.get(k) ?? 0) + 1);
    }

    let num = 0;
    let den = 0;
    for (const r of usable) {
      // Use the row's own unweighted point, which now reflects any manual
      // override, rather than re-deriving from the letter.
      const p = Number(r.unweightedPoint);
      if (Number.isNaN(p)) continue;

      let weight: number;
      if (r.courseType === 'COLLEGE') {
        const k = `${r.gpaForecastId}::${r.seqNo}::${r.course.trim().toLowerCase()}`;
        weight = 1 / Math.max(collegeTerms.get(k) ?? 1, 1);
      } else {
        const c = Number(r.rawCredit);
        if (Number.isNaN(c) || c <= 0) continue;
        const anchor = anchors.get(r.gpaForecastId);
        weight = anchor && anchor > 0 ? c / anchor : c;
      }

      num += p * weight;
      den += weight;
    }
    return { gpa: den === 0 ? null : Math.round((num / den) * 100) / 100, count: usable.length };
  };

  const statusFor = (grade: number): 'locked' | 'in-progress' | 'planned' => {
    const list = rowsByGrade[grade] ?? [];
    if (list.length === 0) return 'planned';
    return list.some((r) => !isEditableGradeStatus(r.rawGrade)) ? 'in-progress' : 'locked';
  };

  if (!studentId) return null;

  return (
    <div style={pageStyle} data-gm-gpa-v2="">
      <FocusStyles />
      <GPAHeroCard key={heroKey} studentId={studentId} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: T.space3, flexWrap: 'wrap', gap: T.space2 }}>
        <div style={{ fontSize: T.textXs, color: T.inkMuted }}>
          Non-GPA courses are not shown below. Courses come from Courses &amp; Grades.
        </div>
        <button onClick={handleSync} disabled={syncing} style={{ ...secondaryBtnStyle, opacity: syncing ? 0.5 : 1 }}>
          {syncing ? 'Refreshing…' : 'Refresh from Courses & Grades'}
        </button>
      </div>

      {/* Dual-enrollment prompts. Proposed, never applied automatically. */}
      {!loading && duplicatePairs.length > 0 ? (
        <div style={panelStyle}>
          {/*
            D5. This used to be a card with a 3px left border, which broke two
            rules at once: cards have no border, and an accent marker may not be
            clipped by the container's own corner. The panel says what it is
            with a labelled chip instead, so the colour is never carrying the
            meaning on its own.
          */}
          <div style={{ display: 'flex', alignItems: 'center', gap: T.space2, marginBottom: T.space2, flexWrap: 'wrap' }}>
            <h2 style={{ ...panelTitleStyle, fontSize: T.textMd }}>
              Is this the same course taken once?
            </h2>
            <span style={chipInline({ color: T.warningInk, bg: T.warningWash })}>Needs your answer</span>
          </div>
          <p style={{ fontSize: T.textSm, color: T.inkBody, margin: `0 0 ${T.space3}`, lineHeight: 1.6 }}>
            A dual-enrollment course often appears on both transcripts, at two different credit
            values. On the UC and CSU applications you enter it once, as the college course.
            Until you confirm, both are counted.
          </p>
          {duplicatePairs.map((pair) => (
            <div
              key={pair.hs.detailId}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: T.space3,
                padding: `${T.space2} 0`,
                borderTop: `1px solid ${T.hairline}`,
                flexWrap: 'wrap',
              }}
            >
              <div style={{ fontSize: T.textSm, color: T.inkBody }}>
                <strong style={{ color: T.ink }}>{pair.hs.course}</strong> at {pair.hs.schoolName}
                {' · '}
                <strong style={{ color: T.ink }}>{pair.college.course}</strong> at{' '}
                {pair.college.schoolName}
              </div>
              <div style={{ display: 'flex', gap: T.space2 }}>
                <button
                  style={{ ...secondaryBtnStyle, padding: '4px 10px', fontSize: T.textXs }}
                  onClick={() => handleDismissDuplicate(pair.hs.detailId)}
                >
                  They are different
                </button>
                <button
                  style={{ ...addBtnStyle, padding: '4px 10px', fontSize: T.textXs }}
                  onClick={() => confirmPair(pair)}
                >
                  Same course, count once
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {loading ? (
        <div style={{ ...panelStyle, textAlign: 'center', color: T.inkMuted, fontSize: T.textSm }}>
          Loading your courses…
        </div>
      ) : (
        gradesToRender.map((grade) => {
          const list = rowsByGrade[grade] ?? [];
          const subtotal = yearSubtotal(grade);
          const status = statusFor(grade);
          const isCollegePanel = grade === 13;

          return (
            <div key={grade} style={panelStyle}>
              <div style={panelHeaderStyle}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <h2 style={panelTitleStyle}>{GRADE_LABEL[grade]}</h2>
                  <span style={panelStatusStyle(status)}>
                    {status === 'locked' ? 'Complete' : status === 'in-progress' ? 'In progress' : 'Planned'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: T.space3 }}>
                  {subtotal.gpa !== null ? (
                    <span style={yearSubtotalStyle}>
                      Year GPA <span style={yearSubtotalNumberStyle}>{subtotal.gpa.toFixed(2)}</span>
                      <span style={{ marginLeft: '6px', color: T.inkMuted }}>({subtotal.count} courses)</span>
                    </span>
                  ) : null}
                  <button style={addBtnStyle} onClick={() => setAddForGradeNo(grade)}>
                    + Add course
                  </button>
                </div>
              </div>

              {/*
                Per-school settings for this year. The annual-credit toggle is
                the Bishop's fix: that school prints one credit for the whole
                year beside two semester grades, so a literal copy into our
                per-semester fields doubles the credits.
              */}
              {blocksForGrade(grade).map((b) => (
                <div
                  key={b.gpaForecastId}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: T.space3,
                    padding: `${T.space2} 0`,
                    fontSize: T.textXs,
                    color: T.inkMuted,
                    flexWrap: 'wrap',
                  }}
                >
                  <span>
                    <strong style={{ color: T.inkStrong }}>{b.schoolName || 'School not set'}</strong>
                    {b.schedule ? ` · ${b.schedule}` : ''} · {b.gradingScale}
                  </span>
                  <label
                    style={{ display: 'flex', alignItems: 'center', gap: T.space2, cursor: 'pointer' }}
                    title="Tick this when the transcript prints one credit for the whole year rather than one per term."
                  >
                    <input
                      type="checkbox"
                      checked={b.creditsAreAnnual}
                      onChange={() => handleToggleAnnualCredits(b)}
                    />
                    Credits on this transcript are for the whole year
                  </label>
                </div>
              ))}

              {list.length === 0 ? (
                <div style={{ fontSize: T.textSm, color: T.inkMuted, padding: `${T.space3} 0` }}>
                  {isCollegePanel
                    ? 'No college or dual-enrollment courses yet.'
                    : `No courses entered for ${GRADE_LABEL[grade]} yet.`}
                </div>
              ) : (
                <div style={tableScrollStyle}>
                  <table style={tableStyle}>
                    <thead>
                      {/*
                        Two-row header so the two point columns read as one idea.
                        "Grade Points" spans them, with UW and W underneath.
                        Every other column spans both rows.
                      */}
                      <tr>
                        <th rowSpan={2} style={thStyle}>Course</th>
                        <th rowSpan={2} style={thCenterWrapStyle}>Weighted Course?</th>
                        <th rowSpan={2} style={thStyle}>School</th>
                        <th rowSpan={2} style={thStyle}>Term</th>
                        <th rowSpan={2} style={thCenterStyle}>Grade</th>
                        <th rowSpan={2} style={thCenterWrapStyle}>Course Credits</th>
                        <th colSpan={2} style={thGroupStyle}>Grade Points</th>
                        <th rowSpan={2} style={thStyle}>Notes</th>
                        <th rowSpan={2} style={{ ...thStyle, width: '30px' }}></th>
                      </tr>
                      <tr>
                        <th style={thSubStyle} title="Unweighted grade points">UW</th>
                        <th style={thSubStyle} title="Weighted grade points">W</th>
                      </tr>
                    </thead>
                    <tbody>
                      {list.map((row) => (
                        <CourseRowView
                          key={row.detailId}
                          row={row}
                          onChange={(partial) => handleRowChange(row.detailId, partial)}
                          onSave={() => handleRowSave(row.detailId)}
                          onSaveNow={(next) => persistRow(next)}
                          onUndoDuplicate={() => setDuplicateForCourse(row, false)}
                          onDelete={() => handleRowDelete(row.detailId)}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {isCollegePanel && list.length > 0 ? (
                <div style={{ fontSize: T.textXs, color: T.inkMuted, marginTop: T.space2, fontStyle: 'italic' }}>
                  College credits and high school subject credits are two different things and are
                  never added together. One semester of a college course counts as a full year of
                  high school, whatever its unit count, and always counts as weighted. Some Cal
                  State schools apply a 2x weight to college coursework in review, and the card
                  above shows that variant when it changes the number.
                </div>
              ) : null}
            </div>
          );
        })
      )}

      {addForGradeNo !== null ? (
        <AddCourseModal
          gradeNo={addForGradeNo}
          studentId={studentId}
          blocks={blocks}
          seqNo={nextSeqNo(rows, addForGradeNo)}
          onClose={() => setAddForGradeNo(null)}
          onAdded={() => {
            reload();
            setHeroKey((k) => k + 1);
          }}
        />
      ) : null}
    </div>
  );
};

export default GpaForecasterV2;
